import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";

const SECTIONS = ["data", "purpose", "marketing", "retention", "recipients", "rights", "cookies"];
const UPDATED = "2026-10-26";

export default function PrivacyPage() {
  const { t, settings, lang } = useSite();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const company = settings.company_name || "AMS Travel";
  const privacyEmail = settings.privacy_email || settings.email;
  const updated = new Date(UPDATED).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="pt-32 md:pt-40 pb-24 px-6" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <p className="flex items-center gap-2 text-[11px] font-medium tracking-[0.22em] uppercase text-sea" data-testid="privacy-eyebrow"><ShieldCheck size={15} /> GDPR</p>
        <h1 className="font-serif text-4xl md:text-5xl text-ocean mt-4" data-testid="privacy-title">{t("legal.privacy")}</h1>
        <p className="mt-3 text-xs text-ocean/50" data-testid="privacy-updated">{t("legal.updated")}: {updated}</p>
        <p className="mt-8 text-ocean/75 leading-relaxed" data-testid="privacy-intro">{t("legal.intro", { company })}</p>

        <section className="mt-10" data-testid="privacy-section-controller">
          <h2 className="font-serif text-2xl text-ocean">{t("legal.controllerT")}</h2>
          <div className="mt-3 bg-white border border-sand/70 rounded-2xl p-5 text-sm text-ocean/75 leading-relaxed shadow-soft">
            <p className="font-medium text-ocean">{company}</p>
            {settings.legal_entity ? (
              <p className="mt-1 whitespace-pre-line" data-testid="privacy-legal-entity">{settings.legal_entity}</p>
            ) : (
              <p className="mt-1 italic text-ocean/55" data-testid="privacy-legal-entity-pending">{t("legal.controllerPending")}</p>
            )}
            {settings.address && <p className="mt-1">{settings.address}</p>}
          </div>
        </section>

        {SECTIONS.map((s) => (
          <section key={s} className="mt-10" data-testid={`privacy-section-${s}`}>
            <h2 className="font-serif text-2xl text-ocean">{t(`legal.${s}T`)}</h2>
            <p className="mt-3 text-ocean/75 leading-relaxed">{t(`legal.${s}`)}</p>
          </section>
        ))}

        <section className="mt-12 bg-ivory border border-sand/70 rounded-3xl p-6 md:p-8" data-testid="privacy-contact">
          <h2 className="font-serif text-2xl text-ocean">{t("legal.contactT")}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {privacyEmail && <li><a href={`mailto:${privacyEmail}`} className="flex items-center gap-2 text-sea hover:text-ocean" data-testid="privacy-contact-email"><Mail size={15} />{privacyEmail}</a></li>}
            {settings.phone && <li><a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sea hover:text-ocean" data-testid="privacy-contact-phone"><Phone size={15} />{settings.phone}</a></li>}
          </ul>
          <Link to="/contact" className="inline-block mt-6 px-6 py-3 bg-ocean text-ivory text-xs font-medium tracking-wider uppercase rounded-xl btn-lift" data-testid="privacy-contact-cta">{t("nav.contact")}</Link>
        </section>
      </div>
    </div>
  );
}
